import { useCallback, useEffect, useState } from 'react';
import { Button, message, Modal, Space } from 'antd';
import { LockOutlined, SafetyOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import ParamListView from '../../../components/ParamListView';
import { getAllRoles } from '../../../services/adminApi';
import RolePermPanel from './components/RolePermPanel';
import ModuleFieldConfigEditor from './components/ModuleFieldConfigEditor';
import '../../../styles/UserSettingsSection.css';

const CONFIG_TYPES = [
  { key: 'listView',    label: 'List View'   },
  { key: 'filter',      label: 'Filter'      },
  { key: 'form',        label: 'Form'        },
  { key: 'permissions', label: 'Permissions' },
];

export default function RolesSection() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [modalOpen, setModalOpen]       = useState(false);
  const [configType, setConfigType]     = useState('listView');
  const [roles, setRoles]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllRoles();
      setRoles(result.roles);
      setTotal(result.total);
    } catch {
      message.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  function openConfig(role, tab = 'listView') {
    setSelectedRole(role);
    setConfigType(tab);
    setModalOpen(true);
  }

  const fields = [
    { label: 'Role Name',   value: 'roleName',   width: 200 },
    { label: 'Description', value: 'description', width: 240 },
    { label: 'Users',       value: 'userCount',   width: 70, sorter: false },
    {
      label: 'Actions', value: '_configure', width: 200, sorter: false,
      render: (_, record) => (
        <Space size={2}>
          <Button size="small" type="link" onClick={() => openConfig(record)}>Configure</Button>
          <Button
            size="small"
            type="link"
            icon={<LockOutlined />}
            onClick={() => openConfig(record, 'permissions')}
          >
            Permissions
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <AppCard icon={<SafetyOutlined />} title="Roles"
        style={{ marginBottom: 0 }}
        bodyStyle={{ padding: 0 }}>
        <ParamListView
          listName="Roles"
          dataSource={roles}
          fields={fields}
          loading={loading}
          total={total}
        />
      </AppCard>

      <Modal
        className="uss-modal"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SafetyOutlined style={{ color: 'var(--color-brand)' }} />
            <span>Configure: {selectedRole?.roleName}</span>
          </div>
        }
        width={720} footer={null} destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <div className="uss-config-toggle">
            {CONFIG_TYPES.map((ct) => (
              <button key={ct.key}
                className={`uss-config-btn${configType === ct.key ? ' active' : ''}`}
                onClick={() => setConfigType(ct.key)}>{ct.label}</button>
            ))}
          </div>
        </div>
        {selectedRole && (
          configType === 'permissions' ? (
            <RolePermPanel key={selectedRole?.roleId} role={selectedRole} />
          ) : (
            <ModuleFieldConfigEditor
              key={`${selectedRole?.roleId}-${configType}`}
              role={selectedRole}
              configType={configType}
            />
          )
        )}
      </Modal>
    </>
  );
}